/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useAiProfilesList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for fetching AI profiles list data.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { useContextStore } from '../../../store/context';
import { getAiProfiles } from '../api/aiProfilesApi';
import type { DataGridFetchResult } from '../../../hooks/useDataGrid';

interface UseAiProfilesListProps {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

export function useAiProfilesList(props: UseAiProfilesListProps = {}) {
  const { showError } = useNotifications();
  const { t } = useI18n();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = props.domainId ?? selectedDomain?.id;

  const fetchProfiles = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      const response = await getAiProfiles({
        page: params.page,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        search: params.search,
        domainId: effectiveDomainId
      });

      // Filter out any undefined or null items
      if (Array.isArray(response.data)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        response.data = response.data.filter((profile: any) =>
          profile && typeof profile === 'object' && profile.id
        );
      }

      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format');
      }

      if (!Array.isArray(response.data)) {
        response.data = [];
      }

      if (!response.pagination) {
        response.pagination = {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0
        };
      }

      return {
        data: response.data || [],
        pagination: response.pagination || {
          page: params.page,
          limit: params.limit,
          total: 0,
          totalPages: 0
        }
      } as DataGridFetchResult;
    } catch (error) {
      showError(t('aiProfiles.messages.loadError'));
      throw error;
    }
  }, [effectiveDomainId, showError, t]);

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && props.onRefreshNeeded) {
      props.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return { fetchProfiles, effectiveDomainId };
}

